import React, { useEffect, useMemo } from "react";
import { ISelectProps, SelectInput } from "~/components/Controls/Select";
import { FormField, hasError, IFieldProps, onChangeField } from "~/components/Form";
import { TableSortDirection } from "~/components/Table";
import { Intent } from "~/lib/intent";
import { sortByString } from "~/lib/sort";
import { PresetSide } from "~/modules/Dictionary/models/Preset";
import { usePresetsQuery } from "~/modules/Dictionary/queries";

export type ISelectPresetProps = Omit<ISelectProps<number | null>, "items"> & {
  side: PresetSide;
  preselectDefault?: boolean;
};

export const SelectPreset: React.FC<ISelectPresetProps> = ({ side, intent, preselectDefault, ...props }) => {
  const { data: presets = [], isPending } = usePresetsQuery();

  const defaultPreset = presets.find((p) => p.side === side && p.name.toLowerCase() === side);
  const { value, onChange } = props;
  useEffect(() => {
    if (preselectDefault && !isPending && value == null && defaultPreset) {
      onChange?.(defaultPreset.id);
    }
  }, [isPending]);

  const items = useMemo(
    () =>
      sortByString(
        presets.filter((p) => p.side === side).map((p) => ({ label: p.name, value: p.id })),
        "label",
        TableSortDirection.ASC
      ),
    [presets, side]
  );

  return (
    <SelectInput
      {...props}
      intent={intent}
      items={items}
      value={props.value ?? null}
      clearable={!preselectDefault}
      searchable
      placeholder={props.placeholder ?? "Select preset"}
      rightIcon={isPending && !props.disabled ? <i className="fa fa-spinner fa-spin" /> : props.rightIcon}
    />
  );
};

export const SelectPresetField = (props: IFieldProps<ISelectPresetProps>) => (
  <FormField {...props}>
    <SelectPreset
      {...props.options}
      intent={hasError(props) && Intent.DANGER}
      value={props.field.value}
      onChange={(val) => onChangeField(props, val)}
    />
  </FormField>
);
